import React, { useState } from 'react';
import { Check, Star, BookOpen, ShieldCheck, ArrowRight, AlertTriangle, ChevronDown, HelpCircle } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { SocialProofPopup } from './components/SocialProofPopup';
import { MetaPixel } from './components/MetaPixel';
// Image Constants
const MAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/kBkq7XKtbKtiiCqo4Tb8gh/1_-_Principal_Cover_1.png';
const BONO_1 = 'https://cdn.magicpatterns.com/uploads/coY32airVPRwCevnKH9EiT/bono_1.png';
const BONO_2 = 'https://cdn.magicpatterns.com/uploads/huq4gdoro8cLtRrapQmyuH/bono_2.png';
const BONO_3 = 'https://cdn.magicpatterns.com/uploads/5HLTMdcQfJB8oj7u1tin3M/bono_3.png';
const BONO_4 = 'https://cdn.magicpatterns.com/uploads/mkGdfjJGzTJiFP4mDdh3bG/bono_4.png';
const CHECKOUT_URL = 'https://pay.hotmart.com/I104220738I?checkoutMode=10';
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const scrollToPurchase = () => {
    const element = document.getElementById('compra');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const faqs = [{
    question: '¿Cómo recibo el material?',
    answer: 'Inmediatamente después de tu compra, recibes un correo con acceso a la plataforma donde puedes descargar todos los archivos en PDF. El acceso es de por vida.'
  }, {
    question: '¿Sirve para cualquier materia y nivel?',
    answer: 'Sí. El Sistema Docente Listo está diseñado para funcionar con cualquier asignatura (Matemáticas, Lengua, Ciencias, Historia, Inglés, etc.) y cualquier nivel educativo (primaria, secundaria, preparatoria).'
  }, {
    question: '¿Qué pasa si no me funciona?',
    answer: 'Tienes 7 días de garantía. Si por cualquier razón no estás satisfecho/a, te devolvemos el 100% de tu dinero sin preguntas.'
  }, {
    question: '¿Necesito conocimientos técnicos?',
    answer: 'No. Todo el material viene en formato PDF listo para imprimir o usar en digital. Solo necesitas descargar e implementar.'
  }, {
    question: '¿Puedo compartirlo con otros docentes?',
    answer: 'La licencia es personal. Sin embargo, puedes recomendar el producto a tus colegas para que ellos también accedan a su propia copia.'
  }];
  return <div className="min-h-screen bg-[#FDFBF7] font-sans text-gray-800 pt-12" data-id="element-0">
      <MetaPixel data-id="element-1" />
      <CountdownTimer data-id="element-2" />
      <SocialProofPopup data-id="element-3" />

      {/* Hero Section */}
      <header className="relative bg-gradient-to-b from-[#3E2723] to-[#5D4037] text-white overflow-hidden" data-id="element-4">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-10 mix-blend-overlay" data-id="element-5"></div>
        <div className="max-w-6xl mx-auto px-4 py-12 md:py-20 relative z-10" data-id="element-6">
          <div className="flex flex-col md:flex-row items-center gap-10" data-id="element-7">
            <div className="flex-1 text-center md:text-left" data-id="element-8">
              <div className="inline-block bg-[#FFD700] text-[#3E2723] font-bold px-4 py-1 rounded-full text-sm mb-6 shadow-lg transform -rotate-1" data-id="element-9">
                📘 SISTEMA DOCENTE LISTO
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 text-[#FFF8E1] drop-shadow-md" data-id="element-10">
                El kit que te resuelve la clase aunque no tengas tiempo
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 font-light" data-id="element-11">
                Herramientas prácticas para cualquier materia y nivel. Deja de
                sobrevivir a tus clases y empieza a conducirlas.
              </p>
              <button onClick={scrollToPurchase} className="group bg-[#D32F2F] hover:bg-[#B71C1C] text-white text-xl font-bold py-4 px-8 rounded-lg shadow-xl transition-all transform hover:scale-105 flex items-center justify-center gap-2 w-full md:w-auto mx-auto md:mx-0" data-id="element-12">
                ¡Quiero dejar de improvisar!
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-13" />
              </button>
            </div>
            <div className="flex-1 relative group cursor-pointer" onClick={scrollToPurchase} data-id="element-14">
              <div className="absolute inset-0 bg-[#FFD700] rounded-xl blur-2xl opacity-20 group-hover:opacity-30 transition-opacity" data-id="element-15"></div>
              <img src={MAIN_IMAGE} alt="Sistema Docente Listo Kit" className="relative w-full max-w-lg mx-auto rounded-lg shadow-2xl transform group-hover:-translate-y-2 transition-transform duration-300 object-contain" data-id="element-16" />
            </div>
          </div>
        </div>
      </header>

      {/* Problem Section */}
      <section className="py-16 px-4 bg-white" data-id="element-17">
        <div className="max-w-3xl mx-auto" data-id="element-18">
          <h2 className="text-3xl font-bold text-[#3E2723] mb-8 text-center" data-id="element-19">
            👩‍🏫 Profe… seamos honestos un segundo.
          </h2>
          <div className="prose prose-lg mx-auto text-gray-600" data-id="element-20">
            <p className="mb-6" data-id="element-21">
              No siempre el problema es el contenido. Es que:
            </p>
            <ul className="space-y-4 list-none pl-0 mb-8" data-id="element-22">
              {['Entras al aula y el grupo está alterado', 'Pierdes 10 minutos en ordenar', 'La actividad no engancha', 'Algunos no participan', 'Tienes que evaluar… pero no sabes con qué criterio'].map((item, i) => <li key={i} className="flex items-start gap-3 bg-red-50 p-4 rounded-lg border-l-4 border-red-400" data-id="element-23">
                  <AlertTriangle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" data-id="element-24" />
                  <span className="font-medium text-gray-800" data-id="element-25">{item}</span>
                </li>)}
            </ul>
            <p className="text-xl font-medium text-center italic text-[#5D4037] mb-8" data-id="element-26">
              Y terminas pensando: "Siento que sobrevivo a la clase… no que la
              conduzco."
            </p>
            <div className="bg-gray-100 p-6 rounded-xl text-center" data-id="element-27">
              <p className="font-bold text-lg mb-2" data-id="element-28">
                👉 No es falta de vocación. <br data-id="element-29" />
                👉 No es falta de capacidad. <br data-id="element-30" />
                <span className="text-[#D32F2F] text-xl" data-id="element-31">
                  👉 Es falta de sistema práctico de aula.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Agitation Section */}
      <section className="py-16 px-4 bg-[#EFEBE9]" data-id="element-32">
        <div className="max-w-4xl mx-auto text-center" data-id="element-33">
          <h2 className="text-3xl font-bold text-[#3E2723] mb-8 flex items-center justify-center gap-3" data-id="element-34">
            <span className="text-red-600 text-4xl" data-id="element-35">❌</span> Lo que nadie te
            enseñó
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto" data-id="element-36">
            Te formaron en contenido. Pero no en:
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-12" data-id="element-37">
            {['Cómo arrancar la clase con control', 'Qué hacer cuando el plan falla', 'Cómo hacer participar a todos', 'Cómo evaluar sin improvisar'].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex items-center gap-4" data-id="element-38">
                <div className="bg-red-100 p-2 rounded-full" data-id="element-39">
                  <span className="text-red-600 font-bold text-xl" data-id="element-40">✕</span>
                </div>
                <span className="text-lg font-medium text-gray-800 text-left" data-id="element-41">
                  {item}
                </span>
              </div>)}
          </div>
          <p className="text-lg text-gray-600 mb-8" data-id="element-42">
            Por eso cada día depende de: tu energía, tu paciencia y tu
            improvisación. <br data-id="element-43" />
            <span className="font-bold text-[#3E2723]" data-id="element-44">Y eso agota.</span>
          </p>
          <button onClick={scrollToPurchase} className="bg-[#3E2723] hover:bg-[#5D4037] text-white font-bold py-3 px-8 rounded-full shadow-lg transition-colors" data-id="element-45">
            Necesito una solución
          </button>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 px-4 bg-white relative overflow-hidden" data-id="element-46">
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-green-400 to-green-600" data-id="element-47"></div>
        <div className="max-w-5xl mx-auto" data-id="element-48">
          <div className="text-center mb-16" data-id="element-49">
            <span className="text-green-600 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-50">
              Presentamos
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#3E2723] mb-6" data-id="element-51">
              ✅ LA SOLUCIÓN: UN SISTEMA DE AULA LISTO PARA USAR
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-52">
              Sistema Docente Listo es un kit de herramientas prácticas para
              cualquier materia y nivel. No importa si das: Matemáticas, Lengua,
              Ciencias, Música, Historia o Inglés.
            </p>
            <p className="mt-4 text-lg font-medium text-[#5D4037]" data-id="element-53">
              Esto no depende del contenido. Depende de cómo funciona la clase.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-54">
            <div className="order-2 md:order-1" data-id="element-55">
              <h3 className="text-2xl font-bold text-[#3E2723] mb-6 flex items-center gap-2" data-id="element-56">
                📦 ¿QUÉ INCLUYE?
              </h3>
              <div className="space-y-6" data-id="element-57">
                {[{
                title: 'TOMO 1 — Control de Aula desde el Primer Minuto',
                desc: 'Rutinas y estrategias para empezar la clase con foco.'
              }, {
                title: 'TOMO 2 — Banco de Actividades de Emergencia',
                desc: 'Dinámicas para cuando sobra tiempo o algo no funciona.'
              }, {
                title: 'TOMO 3 — Sistema de Participación Inmediata',
                desc: 'Estructuras para que todos participen sin presión.'
              }, {
                title: 'TOMO 4 — Kit de Evaluación Rápida Multimateria',
                desc: 'Rúbricas, listas de cotejo y autoevaluaciones listas.'
              }, {
                title: 'TOMO 5 — Dinámicas y Tarjetas Imprimibles',
                desc: 'Recursos listos para usar sin preparar nada.'
              }].map((tomo, i) => <div key={i} className="flex gap-4" data-id="element-58">
                    <div className="bg-[#FFF8E1] w-12 h-12 rounded-lg flex items-center justify-center shrink-0 border border-[#FFD700]" data-id="element-59">
                      <span className="text-2xl" data-id="element-60">🧩</span>
                    </div>
                    <div data-id="element-61">
                      <h4 className="font-bold text-[#3E2723] text-lg" data-id="element-62">
                        {tomo.title}
                      </h4>
                      <p className="text-gray-600" data-id="element-63">{tomo.desc}</p>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="order-1 md:order-2 flex justify-center" data-id="element-64">
              <img src={MAIN_IMAGE} alt="Contenido del Sistema Docente Listo" className="w-full max-w-md rounded-lg shadow-2xl hover:scale-105 transition-transform duration-500 cursor-pointer object-contain" onClick={scrollToPurchase} data-id="element-65" />
            </div>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-16 px-4 bg-[#3E2723] text-white" data-id="element-66">
        <div className="max-w-6xl mx-auto" data-id="element-67">
          <div className="text-center mb-12" data-id="element-68">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#FFD700]" data-id="element-69">
              🎁 BONOS INCLUIDOS
            </h2>
            <p className="text-xl text-gray-300" data-id="element-70">
              ¡GRATIS solo por el día de hoy!
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-71">
            {[{
            img: BONO_1,
            title: 'Frases Docentes que Recuperan el Control'
          }, {
            img: BONO_2,
            title: 'Rutinas de Clase de 5 Minutos'
          }, {
            img: BONO_3,
            title: 'Kit de Participación para Cursos Difíciles'
          }, {
            img: BONO_4,
            title: 'Plantillas de Trabajo en Grupo'
          }].map((bono, i) => <div key={i} className="bg-white/10 backdrop-blur-sm p-3 rounded-2xl border border-white/20 hover:border-[#FFD700] transition-all group cursor-pointer hover:bg-white/15" onClick={scrollToPurchase} data-id="element-72">
                <div className="relative mb-4 overflow-hidden rounded-xl" data-id="element-73">
                  <img src={bono.img} alt={bono.title} className="w-full h-auto object-contain transform group-hover:scale-105 transition-transform duration-500" data-id="element-74" />
                  <div className="absolute top-3 right-3 bg-[#D32F2F] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg" data-id="element-75">
                    GRATIS
                  </div>
                </div>
                <h3 className="font-bold text-center text-[#FFF8E1] text-base px-2" data-id="element-76">
                  {bono.title}
                </h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* Purchase Button After Bonuses */}
      <section id="compra" className="py-16 px-4 bg-gradient-to-b from-[#3E2723] to-[#2D1F1A] text-white" data-id="element-77">
        <div className="max-w-2xl mx-auto" data-id="element-78">
          <div className="bg-white text-gray-900 rounded-3xl p-8 md:p-10 shadow-2xl relative overflow-hidden" data-id="element-79">
            <div className="absolute top-0 right-0 bg-[#D32F2F] text-white text-sm font-bold px-4 py-2 rounded-bl-2xl z-10" data-id="element-80">
              -80% OFF
            </div>

            <div className="text-center mb-6" data-id="element-81">
              <h3 className="text-2xl font-bold text-[#3E2723] mb-2" data-id="element-82">
                📥 ACCESO INMEDIATO
              </h3>
              <p className="text-gray-600" data-id="element-83">
                Descargas todo en segundos. Usas desde hoy mismo.
              </p>
            </div>

            <div className="text-center mb-6" data-id="element-84">
              <p className="text-gray-500 text-sm uppercase tracking-wide font-bold" data-id="element-85">
                Precio Regular:{' '}
                <span className="line-through decoration-red-500 decoration-2" data-id="element-86">
                  $69.99
                </span>
              </p>
              <div className="flex items-center justify-center gap-2 mt-2" data-id="element-87">
                <span className="text-6xl md:text-7xl font-extrabold text-[#3E2723]" data-id="element-88">
                  $13.99
                </span>
                <span className="text-xl text-gray-500 self-end mb-3" data-id="element-89">USD</span>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6" data-id="element-90">
              <p className="text-red-600 font-bold text-base flex items-center justify-center gap-2" data-id="element-91">
                <AlertTriangle className="w-5 h-5" data-id="element-92" />
                Solo quedan 6 cupos a este precio especial
              </p>
            </div>

            <a href={CHECKOUT_URL} className="block w-full bg-[#00C853] hover:bg-[#00B248] text-white text-xl md:text-2xl font-bold py-5 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-6 text-center" data-id="element-93">
              ¡QUIERO MI ACCESO AHORA!
            </a>

            <div className="flex items-center justify-center gap-6 text-sm text-gray-500" data-id="element-94">
              <div className="flex items-center gap-2" data-id="element-95">
                <ShieldCheck className="w-5 h-5 text-green-500" data-id="element-96" />
                Pago Seguro
              </div>
              <div className="flex items-center gap-2" data-id="element-97">
                <BookOpen className="w-5 h-5 text-blue-500" data-id="element-98" />
                Acceso Digital
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Section */}
      <section className="py-16 px-4 bg-[#FFF8E1]" data-id="element-99">
        <div className="max-w-4xl mx-auto" data-id="element-100">
          <h2 className="text-3xl font-bold text-[#3E2723] mb-10 text-center" data-id="element-101">
            🎯 ¿QUÉ CAMBIA DESDE LA PRIMERA SEMANA?
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-102">
            <div className="bg-white p-8 rounded-2xl shadow-sm border-t-4 border-red-500" data-id="element-103">
              <h3 className="text-xl font-bold text-red-600 mb-6 border-b pb-2" data-id="element-104">
                Antes:
              </h3>
              <ul className="space-y-4" data-id="element-105">
                {['Caos al empezar', 'Improvisación constante', 'Cursos pasivos', 'Evaluación "a ojo"'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-600" data-id="element-106">
                    <span className="text-red-500 font-bold" data-id="element-107">❌</span>
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-green-500 transform md:-translate-y-4" data-id="element-108">
              <h3 className="text-xl font-bold text-green-600 mb-6 border-b pb-2" data-id="element-109">
                Después:
              </h3>
              <ul className="space-y-4" data-id="element-110">
                {['Control desde el inicio', 'Actividades listas cuando algo falla', 'Participación real', 'Evaluación clara y rápida'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-800 font-medium" data-id="element-111">
                    <Check className="w-5 h-5 text-green-500" data-id="element-112" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-113">
            <p className="text-xl text-[#5D4037] italic mb-8" data-id="element-114">
              💬 Esto no te convierte en otro docente. <br data-id="element-115" />
              <span className="font-bold" data-id="element-116">
                Te devuelve el control que ya tenías, pero con estructura.
              </span>
            </p>
            <button onClick={scrollToPurchase} className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold py-3 px-10 rounded-lg shadow-lg transition-all transform hover:scale-105" data-id="element-117">
              ¡SÍ! Quiero el control de mi aula
            </button>
          </div>
        </div>
      </section>

      {/* Ideal For Section */}
      <section className="py-16 px-4 bg-white" data-id="element-118">
        <div className="max-w-3xl mx-auto text-center" data-id="element-119">
          <h2 className="text-2xl font-bold text-[#3E2723] mb-8" data-id="element-120">
            ⏳ Ideal si:
          </h2>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-121">
            {['Sientes que siempre corres atrás', 'Trabajas en varias escuelas', 'Tienes muchos cursos', 'Terminas agotado/a', 'Quieres que la clase funcione sin gritar ni improvisar'].map((item, i) => <span key={i} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full border border-gray-200 font-medium" data-id="element-122">
                {item}
              </span>)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4 bg-[#EFEBE9]" data-id="element-123">
        <div className="max-w-6xl mx-auto" data-id="element-124">
          <h2 className="text-3xl font-bold text-[#3E2723] mb-12 text-center" data-id="element-125">
            Lo que dicen otros docentes
          </h2>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-126">
            {[{
            name: 'María González',
            role: 'Profesora de Matemáticas, 34 años',
            text: 'Antes perdía 15 minutos solo tratando de que se sentaran. Con las rutinas del Tomo 1, entran y saben qué hacer. Recuperé mi voz y mi tiempo.'
          }, {
            name: 'Carlos Mendoza',
            role: 'Profesor de Historia, 42 años',
            text: 'Tengo 6 cursos diferentes y vivía planificando los fines de semana. Las actividades de emergencia me salvaron la vida más de una vez.'
          }, {
            name: 'Laura Rojas',
            role: 'Maestra de Primaria, 29 años',
            text: 'Mis alumnos no participaban por vergüenza. El sistema de participación sin presión cambió la dinámica del aula por completo.'
          }, {
            name: 'Patricia Silva',
            role: 'Profesora de Inglés, 38 años',
            text: 'Evaluar oralmente era un caos. Con las rúbricas del kit, es rápido y los chicos entienden perfectamente su nota.'
          }, {
            name: 'Roberto Díaz',
            role: 'Profesor de Ciencias, 45 años',
            text: 'Lo compré dudando, pero es lo más práctico que he visto. No es teoría aburrida, son herramientas para usar ya.'
          }].map((testimonio, i) => <div key={i} className="bg-white p-6 rounded-xl shadow-sm" data-id="element-127">
                <div className="flex text-[#FFD700] mb-3" data-id="element-128">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-129" />)}
                </div>
                <p className="text-gray-600 mb-4 italic" data-id="element-130">"{testimonio.text}"</p>
                <div className="border-t pt-3" data-id="element-131">
                  <p className="font-bold text-[#3E2723]" data-id="element-132">{testimonio.name}</p>
                  <p className="text-xs text-gray-500" data-id="element-133">{testimonio.role}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-[#3E2723] to-[#212121] text-white text-center" data-id="element-134">
        <div className="max-w-2xl mx-auto" data-id="element-135">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-136">
            🚀 Entra ahora y deja de sobrevivir a tus clases
          </h2>
          <p className="text-xl text-gray-300 mb-8" data-id="element-137">
            Es momento de conducirlas con sistema.
          </p>
          <a href={CHECKOUT_URL} className="inline-block bg-[#00C853] hover:bg-[#00B248] text-white text-xl md:text-2xl font-bold py-5 px-10 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1" data-id="element-138">
            ¡QUIERO MI ACCESO AHORA! - $13.99
          </a>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-[#FDFBF7]" data-id="element-139">
        <div className="max-w-3xl mx-auto" data-id="element-140">
          <div className="text-center mb-12" data-id="element-141">
            <HelpCircle className="w-12 h-12 text-[#3E2723] mx-auto mb-4" data-id="element-142" />
            <h2 className="text-3xl font-bold text-[#3E2723]" data-id="element-143">
              Preguntas Frecuentes
            </h2>
          </div>

          <div className="space-y-4" data-id="element-144">
            {faqs.map((faq, i) => <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-145">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors" data-id="element-146">
                  <span className="font-bold text-[#3E2723] text-lg" data-id="element-147">
                    {faq.question}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-148" />
                </button>
                {openFaq === i && <div className="px-6 pb-5 text-gray-600 border-t border-gray-100 pt-4" data-id="element-149">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-gray-500 py-8 px-4 text-center text-sm" data-id="element-150">
        <div className="max-w-6xl mx-auto" data-id="element-151">
          <p className="mb-4" data-id="element-152">
            🚀 Entra ahora y deja de sobrevivir a tus clases. Es momento de
            conducirlas con sistema.
          </p>
          <p data-id="element-153">
            &copy; {new Date().getFullYear()} Sistema Docente Listo. Todos los
            derechos reservados.
          </p>
          <p className="mt-2 text-xs text-gray-600" data-id="element-154">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio no está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>
    </div>;
}